/**
 * Token custody.
 *
 * The access token lives in memory only: anything in localStorage is readable by
 * any script that gets injected, and a 15-minute bearer is the single most
 * valuable thing on the page. The refresh token has to survive a reload, so it
 * does go to localStorage — the accepted trade-off for a bearer-token PWA, and
 * the reason refresh rotation with reuse detection exists on the server.
 *
 * Reuse detection also means refreshing must be **single-flight**: if three
 * requests hit a 401 at once and each rotates, the server sees two replays of a
 * spent token and revokes the whole family. One refresh, everyone waits.
 */

const REFRESH_KEY = 'refresh_token'

export interface Tokens {
  accessToken: string
  refreshToken: string
  expiresIn?: number
}

let accessToken: string | null = null
let listeners = new Set<(authenticated: boolean) => void>()

function readStoredRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

let refreshToken: string | null = readStoredRefresh()
/**
 * Whether localStorage is holding the current refresh token. A private window
 * that refuses writes still runs a working single-tab session from memory, and
 * storage must not be believed over memory there.
 */
let storageHoldsToken = refreshToken !== null

function notify(): void {
  const authenticated = refreshToken !== null
  for (const listener of listeners) listener(authenticated)
}

export function getAccessToken(): string | null {
  return accessToken
}

export function getRefreshToken(): string | null {
  return refreshToken
}

export function setTokens(tokens: Tokens): void {
  /*
   * Guarded because the failure is silent and delayed: storing a non-string
   * here writes "undefined" into localStorage and the session only dies on the
   * request after next, far from the cause.
   */
  if (typeof tokens.accessToken !== 'string' || typeof tokens.refreshToken !== 'string') {
    throw new TypeError('setTokens requires string tokens')
  }

  accessToken = tokens.accessToken
  refreshToken = tokens.refreshToken
  try {
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken)
    storageHoldsToken = true
  } catch {
    // A private window still gets a working session for this tab.
    storageHoldsToken = false
  }
  notify()
}

export function clearTokens(): void {
  accessToken = null
  refreshToken = null
  storageHoldsToken = false
  try {
    localStorage.removeItem(REFRESH_KEY)
  } catch {
    // Nothing to clear.
  }
  notify()
}

/**
 * The refresh token as it is *right now*, storage first.
 *
 * Every tab of the app shares one refresh token through localStorage, and the
 * `storage` event that tells this tab another one rotated it is delivered on
 * its own schedule — it can arrive after this tab has already decided to
 * present the copy it holds in memory. Read under the cross-tab refresh lock,
 * storage is the truth: a newer token there is adopted, and a missing one
 * means another tab signed out, which this tab honours on the spot.
 */
export function latestRefreshToken(): string | null {
  if (!storageHoldsToken) return refreshToken
  let stored: string | null
  try {
    stored = localStorage.getItem(REFRESH_KEY)
  } catch {
    return refreshToken
  }
  if (stored !== refreshToken) {
    refreshToken = stored
    if (stored === null) accessToken = null
    notify()
  }
  return stored
}

export function isAuthenticated(): boolean {
  return refreshToken !== null
}

/** Fires whenever the session appears or disappears, including in another tab. */
export function onSessionChange(listener: (authenticated: boolean) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Signing out in one tab must sign out in all of them. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== REFRESH_KEY) return
    refreshToken = event.newValue
    storageHoldsToken = event.newValue !== null
    if (event.newValue === null) accessToken = null
    notify()
  })
}

/** Test seam. */
export function __resetSession(): void {
  accessToken = null
  refreshToken = null
  storageHoldsToken = false
  listeners = new Set()
}
